import React, { useEffect, useState, useRef } from "react";
import { Box } from "@mui/material";
import { motion } from "framer-motion";

const gridSize = 3;
const tileSize = 32;
const gap = 4;

// Helper to get pixel position for a cell
const getPosition = (row, col) => ({
  x: col * (tileSize + gap),
  y: row * (tileSize + gap),
});

const isCorrect = (n, row, col, step, animate) => {
  // For a 3x3 grid, correct position is n === row * 3 + col + 1
  if (n == null) return false;
  if (step === 1 && n === 8) {
    // Only green if 8 is in (2,1) and animate is true
    return animate === true && row === 2 && col === 1;
  }
  return n === row * 3 + col + 1;
};

const MiniGridAnimation = ({ step }) => {
  // For step 0 and 1, animate a tile moving into the empty space and back in a loop
  const [animate, setAnimate] = useState(false);
  const intervalRef = useRef();

  useEffect(() => {
    setAnimate(false);
    clearInterval(intervalRef.current);
    if (step === 0 || step === 1) {
      // Start loop after a short delay
      const timeout = setTimeout(() => setAnimate(true), 350);
      intervalRef.current = setInterval(() => {
        setAnimate((prev) => !prev);
      }, 1000);
      return () => {
        clearTimeout(timeout);
        clearInterval(intervalRef.current);
      };
    }
    // Cleanup on unmount or step change
    return () => clearInterval(intervalRef.current);
  }, [step]);

  // Define the grid for each step
  let tiles = [];
  let emptyCell = null;
  if (step === 0) {
    // Animate 5 from (2,1) <-> (1,1)
    tiles = [
      { n: 1, row: 0, col: 0 },
      { n: 2, row: 0, col: 1 },
      { n: 3, row: 0, col: 2 },
      { n: 4, row: 1, col: 0 },
      // empty at (1,1) or (2,1)
      { n: 6, row: 1, col: 2 },
      { n: 7, row: 2, col: 0 },
      { n: 5, row: animate ? 1 : 2, col: 1 },
      { n: 8, row: 2, col: 2 },
    ];
    emptyCell = animate ? { row: 2, col: 1 } : { row: 1, col: 1 };
  } else if (step === 1) {
    // Animate 8 from (2,2) <-> (2,1)
    tiles = [
      { n: 1, row: 0, col: 0 },
      { n: 2, row: 0, col: 1 },
      { n: 3, row: 0, col: 2 },
      { n: 4, row: 1, col: 0 },
      { n: 5, row: 1, col: 1 },
      { n: 6, row: 1, col: 2 },
      { n: 7, row: 2, col: 0 },
      // empty at (2,1) or (2,2)
      { n: 8, row: animate ? 2 : 2, col: animate ? 1 : 2 },
    ];
    emptyCell = animate ? { row: 2, col: 2 } : { row: 2, col: 1 };
  } else {
    // Solved state
    tiles = [
      { n: 1, row: 0, col: 0 },
      { n: 2, row: 0, col: 1 },
      { n: 3, row: 0, col: 2 },
      { n: 4, row: 1, col: 0 },
      { n: 5, row: 1, col: 1 },
      { n: 6, row: 1, col: 2 },
      { n: 7, row: 2, col: 0 },
      { n: 8, row: 2, col: 1 },
      // empty at (2,2)
    ];
    emptyCell = { row: 2, col: 2 };
  }

  return (
    <Box
      sx={{
        width: gridSize * tileSize + (gridSize - 1) * gap,
        height: gridSize * tileSize + (gridSize - 1) * gap,
        bgcolor: "#F8F5F2",
        borderRadius: 2,
        boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
        position: "relative",
        mx: "auto",
        my: 1,
        p: 0,
        display: "block",
      }}
    >
      {/* Tiles */}
      {tiles.map((tile) => {
        const { x, y } = getPosition(tile.row, tile.col);
        const correct = isCorrect(tile.n, tile.row, tile.col, step, animate);
        return (
          <motion.div
            key={tile.n}
            initial={false}
            animate={{ x, y }}
            transition={{
              type: "spring",
              stiffness: 350,
              damping: 30,
              duration: 0.5,
            }}
            style={{
              position: "absolute",
              width: tileSize,
              height: tileSize,
              zIndex: 2,
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
                bgcolor: correct ? "#E8F5E9" : "#fff",
                border: correct ? "2.5px solid #388e3c" : "2.5px solid #111",
                borderRadius: 1.2,
                fontWeight: 700,
                fontSize: "1rem",
                color: "#111",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: correct ? "0 2px 0 #b2dfdb" : "0 2px 0 #bbb",
                transition: "background 0.2s, border 0.2s, box-shadow 0.2s",
              }}
            >
              {tile.n}
            </Box>
          </motion.div>
        );
      })}
      {/* Empty cell */}
      <Box
        sx={{
          position: "absolute",
          width: tileSize,
          height: tileSize,
          left: getPosition(emptyCell.row, emptyCell.col).x,
          top: getPosition(emptyCell.row, emptyCell.col).y,
          bgcolor: "#E0E0E0",
          border: "2.5px solid #ddd",
          borderRadius: 1.2,
          boxShadow: "0 1px 0 #eee",
          zIndex: 1,
        }}
      />
    </Box>
  );
};

export default MiniGridAnimation;
